/**
 * PitchTransitionSlide
 * Fullscreen overgangsscherm tussen twee pitchers.
 * Toont 10-seconden countdown, naam volgende pitcher en fade-in/-out muziek.
 *
 * Rule 3: listener cleanup via useEffect return.
 * Rule 8: mobiel-first, aanraakdoelen ≥ 44px.
 * Rule 9: geen hardcoded waarden — alles via props of constants.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TRANSITION_DEFAULT_SECONDS, TRANSITION_MUSIC_FADE_SECONDS } from '../meetingConstants';

interface PitchTransitionSlideProps {
  nextPresenterName: string;
  nextSlotNumber: number;
  totalSlots: number;
  durationSeconds?: number;          // standaard 10
  musicUrl?: string | null;          // optionele overgangs-muziek
  musicVolume?: number;              // 0-1, standaard 0.5
  primaryColor?: string;
  accentColor?: string;
  isHost?: boolean;
  onComplete: () => void;            // wordt aangeroepen na aftelling
  onSkip?: () => void;               // host kan overslaan
}

const PitchTransitionSlide: React.FC<PitchTransitionSlideProps> = ({
  nextPresenterName,
  nextSlotNumber,
  totalSlots,
  durationSeconds = TRANSITION_DEFAULT_SECONDS,
  musicUrl,
  musicVolume = 0.5,
  primaryColor = '#1e293b',
  accentColor = '#f97316',
  isHost = false,
  onComplete,
  onSkip,
}) => {
  const [remaining, setRemaining] = useState(durationSeconds);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Muziek fade-in ────────────────────────────────────────────────────────
  const startMusic = useCallback(() => {
    if (!musicUrl) return;
    const audio = new Audio(musicUrl);
    audio.loop = false;
    audio.volume = 0;
    audioRef.current = audio;
    audio.play().catch(() => { /* autoplay policy */ });

    const steps = TRANSITION_MUSIC_FADE_SECONDS * 10; // 100ms per stap
    let step = 0;
    fadeIntervalRef.current = setInterval(() => {
      step++;
      if (audioRef.current) {
        audioRef.current.volume = Math.min(musicVolume, (step / steps) * musicVolume);
      }
      if (step >= steps && fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    }, 100);
  }, [musicUrl, musicVolume]);

  // ── Muziek fade-out ───────────────────────────────────────────────────────
  const fadeOutMusic = useCallback(() => {
    if (!audioRef.current) return;
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    const audio = audioRef.current;
    const startVol = audio.volume;
    const steps = TRANSITION_MUSIC_FADE_SECONDS * 10;
    let step = 0;
    fadeIntervalRef.current = setInterval(() => {
      step++;
      audio.volume = Math.max(0, startVol - (step / steps) * startVol);
      if (step >= steps) {
        audio.pause();
        if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    }, 100);
  }, []);

  // ── Countdown + lifecycle ─────────────────────────────────────────────────
  useEffect(() => {
    setRemaining(durationSeconds);
    startMusic();

    countdownRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        // Fade-out beginnen in de laatste TRANSITION_MUSIC_FADE_SECONDS seconden
        if (prev <= TRANSITION_MUSIC_FADE_SECONDS + 1) fadeOutMusic();
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationSeconds, musicUrl]);

  // ── Roep onComplete aan als countdown nul bereikt ─────────────────────────
  useEffect(() => {
    if (remaining === 0) onComplete();
  }, [remaining, onComplete]);

  const progress = Math.max(0, Math.min(100, ((durationSeconds - remaining) / durationSeconds) * 100));

  const handleSkip = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    (onSkip ?? onComplete)();
  };

  return (
    <div
      className="absolute inset-0 z-[70] flex flex-col items-center justify-center select-none"
      style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, #0f172a 100%)` }}
      aria-live="assertive"
      aria-label={`Overgang — volgende pitcher: ${nextPresenterName}`}
    >
      {/* ── Top label ──────────────────────────────────────────────────────── */}
      <p
        className="text-xs uppercase tracking-[0.25em] font-bold mb-6 opacity-60"
        style={{ color: accentColor }}
      >
        Volgende spreker
      </p>

      {/* ── Slot indicator ─────────────────────────────────────────────────── */}
      <p className="text-sm font-medium text-white/40 mb-2">
        {nextSlotNumber} van {totalSlots}
      </p>

      {/* ── Naam volgende pitcher ───────────────────────────────────────────── */}
      <h2
        className="text-5xl md:text-7xl font-black text-white text-center px-8 leading-tight"
        style={{ textShadow: `0 0 40px ${accentColor}55` }}
      >
        {nextPresenterName}
      </h2>

      {/* ── Countdown getal ────────────────────────────────────────────────── */}
      <div
        className="mt-10 text-8xl font-black tabular-nums"
        style={{ color: remaining <= 3 ? accentColor : 'rgba(255,255,255,0.9)' }}
      >
        {remaining}
      </div>

      {/* ── Progressbalk ───────────────────────────────────────────────────── */}
      <div className="mt-8 w-64 h-1 rounded-full bg-white/15 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${progress}%`, background: accentColor }}
        />
      </div>

      {/* ── Skip knop (host only) ───────────────────────────────────────────── */}
      {isHost && (
        <button
          onClick={handleSkip}
          className="mt-10 min-h-[44px] px-6 rounded-xl text-sm font-semibold border border-white/20 text-white/70 hover:bg-white/10 transition-colors"
          aria-label="Overgang overslaan"
        >
          Overslaan
        </button>
      )}
    </div>
  );
};

export default PitchTransitionSlide;
